"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Search } from "lucide-react";

export default function MicrositeSeoScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0A0814] text-white overflow-hidden">
      <div
        className="absolute bottom-0 left-1/4 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,182,0.18) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
            {t("Easy to find", "Fácil de encontrar")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("A real page ", "Una página real ")}
            <span className="text-white/30">{t("people can find.", "que la gente encuentra.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-white/55 leading-relaxed mt-6">
            {t(
              "Name, hours, location, photos and your upcoming events on one public page, at a link you control. Put it in your bio, your DMs, your flyers. Search engines can find it like any public site.",
              "Nombre, horarios, ubicación, fotos y tus eventos próximos en una sola página pública, en un link que controlas. Ponlo en tu bio, tus DMs, tus flyers. Los buscadores la encuentran como a cualquier sitio público."
            )}
          </p>
        </motion.div>

        {/* Venue page preview mockup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white rounded-[20px] p-6 md:p-8 max-w-[760px] mx-auto text-redy-charcoal"
        >
          {/* Search bar */}
          <div className="flex items-center gap-3 bg-[#FAFAF8] border border-black/5 rounded-full px-4 py-3 mb-6">
            <Search className="w-4 h-4 text-redy-secondary/55" />
            <span className="text-[14px] font-medium text-redy-charcoal/85">
              neonclub.com
            </span>
          </div>

          {/* Result */}
          <div className="space-y-1 mb-3">
            <div className="text-[10px] text-redy-secondary">
              neonclub.com › {t("events", "events")}
            </div>
            <div className="text-[18px] font-bold leading-tight" style={{ color: "#1A0DAB" }}>
              Neon Club — Friday & Saturday in Deep Ellum, Dallas
            </div>
            <div className="text-[13px] text-redy-charcoal/75 leading-relaxed">
              {t(
                "21+ nightclub with weekly house, cumbia and bass nights. Tickets from $40. Open Friday & Saturday, doors 10 PM. NEON RUSH this Friday Dec 13...",
                "Nightclub 21+ con house, cumbia y bass semanales. Tickets desde $40. Viernes y Sábado, puertas 10 PM. NEON RUSH este viernes Dic 13..."
              )}
            </div>
          </div>

          {/* Hours + category */}
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12px] mb-5">
            <div className="flex items-center gap-1.5 text-redy-secondary">
              <span className="font-bold" style={{ color: "#2ECC71" }}>{t("Open Fri 10 PM", "Abre Vie 10 PM")}</span>
            </div>
            <div className="text-redy-secondary">
              <span className="font-bold text-redy-charcoal">$$</span> · {t("Nightlife · 21+", "Nightlife · 21+")}
            </div>
          </div>

          {/* Upcoming events on the page */}
          <div className="pt-5 border-t border-black/5">
            <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-3">
              {t("Upcoming events on your page", "Eventos próximos en tu página")}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { date: "Fri Dec 13", title: "NEON RUSH", price: "$40" },
                { date: "Sat Dec 14", title: "BIRRIA + BASS", price: "Sold out" },
                { date: "Fri Dec 20", title: "ARCADE NIGHT", price: "$25" },
              ].map((ev, i) => (
                <motion.div
                  key={ev.title}
                  initial={{ opacity: 0, y: 6 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.4, delay: 0.4 + 0.1 * i }}
                  className="bg-[#FAFAF8] border border-black/5 rounded-[10px] p-2.5"
                >
                  <div className="text-[9px] font-black uppercase tracking-wider text-redy-secondary/60">
                    {ev.date}
                  </div>
                  <div className="text-[11px] font-black text-redy-charcoal truncate">{ev.title}</div>
                  <div className="text-[10px] font-bold tabular-nums" style={{ color: "#7C3AED" }}>
                    {ev.price}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
